import React, {
  FC,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Divider, Typography } from "@mui/material";

import ChatThumbnail from "./ChatThumbnail";
import ClosedChatsHintRow from "./ClosedChatsHintRow";
import {
  ChatListOuterElement,
  createChatTreeDropCursor,
  FlatChatRow,
  channelToId,
  useBlurArboristOnModalOpen,
  useIsMuiModalOpen,
  useListContainerHeight,
  CLOSED_CHATS_HINT_ID,
  CLOSED_CHATS_HINT_ROW,
  CLOSED_CHATS_HINT_ROW_HEIGHT,
  type VirtualizedChatListHandle,
} from "./chatVirtualizedTree";
import { IChannel } from "../../../types/chat";
import { useAuth } from "../../../contexts/AuthContext";
import {
  SimpleTree,
  Tree,
  type DragPreviewProps,
  type MoveHandler,
  type NodeApi,
  type TreeApi,
} from "react-arborist";
import { DEFAULT_CHAT_ROW_HEIGHT } from "../../../types/sidebar";
import { NoChatsContentMessage } from "./NoChatsContentMessage";
import { LoadingChatsMessage } from "./LoadingChatsMessage";

const noop = () => {};

interface ChatTreeDragPreviewProps extends DragPreviewProps {
  chats: IChannel[];
  chatNotifications: Set<string>;
  listWidth: number;
}

const ChatTreeDragPreview: FC<ChatTreeDragPreviewProps> = ({
  offset,
  id,
  isDragging,
  chats,
  chatNotifications,
  listWidth,
}) => {
  if (!isDragging || !offset || !id) {
    return null;
  }

  const previewWidth = listWidth > 0 ? listWidth : 280;

  const chat = chats.find((channel) => channelToId(channel) === id);
  if (!chat) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        pointerEvents: "none",
        zIndex: 9999,
        left: 0,
        top: 0,
        transform: `translate(${offset.x}px, ${offset.y}px)`,
        width: previewWidth,
        maxWidth: previewWidth,
        overflow: "hidden",
        cursor: "grabbing",
      }}
    >
      <ChatThumbnail
        chat={chat}
        isDragging
        onChatDeleted={noop}
        onChatPinned={noop}
        onChatSelected={noop}
        hasNotification={chatNotifications.has(chat.name)}
      />
    </Box>
  );
};

const reorderFlatChannels = (
  chats: IChannel[],
  dragIds: string[],
  targetIndex: number,
): IChannel[] => {
  const tree = new SimpleTree(
    chats.map((chat) => ({ ...chat, id: channelToId(chat) })),
  );

  for (const id of dragIds) {
    tree.move({ id, parentId: null, index: targetIndex });
  }

  return tree.data;
};

interface ChatListRendererProps {
  chatChannels: IChannel[];
  chatNotifications: Set<string>;
  onChatDeleted: (chatName: string) => void;
  onChatPinned: (chatName: string, isPinned: boolean) => void;
  onChatSelected: (chatId: string, claimId: string | undefined) => void;
  onReorderChannels: (orderedChannels: IChannel[]) => void;
  chatListRef: React.RefObject<HTMLDivElement | null>;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  loading: boolean;
  loadingMore: boolean;
  /** Changes when sidebar panels below the chat list appear or disappear. */
  layoutKey?: string;
  showPinned?: boolean;
  filterByClaimId?: string | null;
  noDataTitleMsg?: string;
  noDataSubtitleMsg?: string;
  /** Show a hint after the last chat pointing users to the filters panel,
   * since closed chats are hidden from the list whenever this is true. */
  showClosedChatsHint?: boolean;
}

const dragIdsBelongToList = (dragIds: string[], allowedIds: Set<string>) =>
  dragIds.every((id) => allowedIds.has(id));

type ListDropGuard = (args: {
  parentNode: NodeApi<IChannel>;
  dragNodes: NodeApi<IChannel>[];
  index: number;
}) => boolean;

const createListDropGuard =
  (allowedIds: Set<string>, isModalOpen: boolean): ListDropGuard =>
  ({ dragNodes }) => {
    if (isModalOpen) {
      // true => drop disabled, false => drop allowed
      return true;
    }

    return dragNodes.some((node) => !allowedIds.has(channelToId(node.data)));
  };

const ChatListRendererContent = forwardRef<
  VirtualizedChatListHandle,
  ChatListRendererProps
>(function ChatListRendererContent(
  {
    chatChannels: _chatChannels,
    chatNotifications,
    onChatDeleted,
    onChatPinned,
    onChatSelected,
    onReorderChannels,
    chatListRef,
    onScroll,
    loading,
    loadingMore,
    layoutKey = "",
    showPinned,
    filterByClaimId,
    noDataTitleMsg,
    noDataSubtitleMsg,
    showClosedChatsHint,
  },
  ref,
) {
  const { user } = useAuth();

  // If other channels are pinned, mark them as not pinned to see them in "Shared chats"
  const chatChannels = useMemo(
    () =>
      _chatChannels?.map((chat) =>
        chat.user_email && chat.user_email !== user?.email
          ? { ...chat, pinned: false }
          : chat,
      ) ?? [],
    [_chatChannels, user?.email],
  );

  const pinnedChats = useMemo(
    () => chatChannels.filter((chat) => chat.pinned),
    [chatChannels],
  );
  const regularChats = useMemo(
    () => chatChannels.filter((chat) => !chat.pinned),
    [chatChannels],
  );
  const dragRef = useRef(false);
  const isModalOpen = useIsMuiModalOpen();
  const {
    containerRef: measureContainerRef,
    height,
    width: listWidth,
  } = useListContainerHeight([layoutKey]);
  const {
    containerRef: pinnedScrollMeasureRef,
    height: pinnedHeight,
    width: pinnedListWidth,
  } = useListContainerHeight([layoutKey]);
  const treeRef = useRef<TreeApi<IChannel>>(null);
  const pinnedTreeRef = useRef<TreeApi<IChannel>>(null);
  useBlurArboristOnModalOpen(isModalOpen, treeRef, pinnedTreeRef);

  useImperativeHandle(
    ref,
    () => ({
      scrollToTop: () => {
        [treeRef, pinnedTreeRef].forEach((treeApiRef) => {
          const list = treeApiRef.current?.list.current;
          if (list) {
            list.scrollTo(0);
            return;
          }

          const el = treeApiRef.current?.listEl.current;
          if (el) {
            el.scrollTop = 0;
          }
        });
      },
    }),
    [],
  );
  const pinnedListRef = useRef<HTMLDivElement | null>(null);
  const ownListRef = useRef<HTMLDivElement | null>(null);
  const [dndRootElement, setDndRootElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [pinnedDndRootElement, setPinnedDndRootElement] =
    useState<HTMLDivElement | null>(null);

  const resolvedPinnedListWidth =
    pinnedListWidth > 0 ? pinnedListWidth : listWidth;

  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      ownListRef.current = node;
      setDndRootElement(node);
      measureContainerRef(node);
    },
    [measureContainerRef],
  );

  const pinnedContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      pinnedListRef.current = node;
      setPinnedDndRootElement(node);
      pinnedScrollMeasureRef(node);
    },
    [pinnedScrollMeasureRef],
  );

  // Split regular chats into own and others (only relevant when claim-filtered)
  const hasOtherUsers = regularChats.some(
    (chat) => chat.user_email && chat.user_email !== user?.email,
  );
  const ownChats = hasOtherUsers
    ? regularChats.filter(
        (chat) => !chat.user_email || chat.user_email === user?.email,
      )
    : regularChats;

  const ownTreeData = useMemo(
    () =>
      showClosedChatsHint && ownChats.length > 0
        ? [...ownChats, CLOSED_CHATS_HINT_ROW]
        : ownChats,
    [ownChats, showClosedChatsHint],
  );

  const otherRegularChats = hasOtherUsers
    ? regularChats.filter(
        (chat) => chat.user_email && chat.user_email !== user?.email,
      )
    : [];

  const pinnedTreeData = useMemo(
    () =>
      showClosedChatsHint && pinnedChats.length > 0
        ? [...pinnedChats, CLOSED_CHATS_HINT_ROW]
        : pinnedChats,
    [pinnedChats, showClosedChatsHint],
  );

  const pinnedChatIds = useMemo(
    () => new Set(pinnedChats.map(channelToId)),
    [pinnedChats],
  );
  const ownChatIds = useMemo(
    () => new Set(ownChats.map(channelToId)),
    [ownChats],
  );

  // Chats matching the active claim filter are sorted to the top of ownChats
  // (see Sidebar's chatList memo), so they form one contiguous run. Track
  // which end(s) of that run each matching chat is on, so it can be
  // rendered as a single rounded, tinted group instead of a hard divider.
  const claimGroupEdges = useMemo(() => {
    const edges = new Map<string, { isFirst: boolean; isLast: boolean }>();

    if (!filterByClaimId) {
      return edges;
    }

    const matchIndexes = ownChats.reduce<number[]>((acc, chat, index) => {
      if (chat.claim_number === filterByClaimId) {
        acc.push(index);
      }
      return acc;
    }, []);

    if (matchIndexes.length === 0) {
      return edges;
    }

    const firstIndex = matchIndexes[0];
    const lastIndex = matchIndexes[matchIndexes.length - 1];

    matchIndexes.forEach((index) => {
      edges.set(channelToId(ownChats[index]), {
        isFirst: index === firstIndex,
        isLast: index === lastIndex,
      });
    });

    return edges;
  }, [ownChats, filterByClaimId]);

  const disablePinnedDrop = useMemo(
    () => createListDropGuard(pinnedChatIds, isModalOpen),
    [pinnedChatIds, isModalOpen],
  );
  const disableOwnDrop = useMemo(
    () => createListDropGuard(ownChatIds, isModalOpen),
    [ownChatIds, isModalOpen],
  );

  const pinnedDropCursor = useMemo(
    () => createChatTreeDropCursor(pinnedChatIds, pinnedListRef),
    [pinnedChatIds],
  );
  const ownDropCursor = useMemo(
    () => createChatTreeDropCursor(ownChatIds, ownListRef),
    [ownChatIds],
  );

  const handlePinnedTreeMove = useCallback<MoveHandler<IChannel>>(
    (args) => {
      if (
        isModalOpen ||
        args.parentId !== null ||
        args.dragIds.length === 0 ||
        !dragIdsBelongToList(args.dragIds, pinnedChatIds)
      ) {
        return;
      }

      dragRef.current = true;

      const newOrderedPinned = reorderFlatChannels(
        pinnedChats,
        args.dragIds,
        args.index,
      );

      onReorderChannels([...newOrderedPinned, ...regularChats]);

      setTimeout(() => {
        dragRef.current = false;
      }, 500);
    },
    [isModalOpen, pinnedChats, regularChats, onReorderChannels, pinnedChatIds],
  );

  const handleTreeMove = useCallback<MoveHandler<IChannel>>(
    (args) => {
      if (
        isModalOpen ||
        args.parentId !== null ||
        args.dragIds.length === 0 ||
        !dragIdsBelongToList(args.dragIds, ownChatIds)
      ) {
        return;
      }

      dragRef.current = true;

      const newOrderedOwn = reorderFlatChannels(
        ownChats,
        args.dragIds,
        args.index,
      );

      onReorderChannels([
        ...pinnedChats,
        ...newOrderedOwn,
        ...otherRegularChats,
      ]);

      setTimeout(() => {
        dragRef.current = false;
      }, 500);
    },
    [
      isModalOpen,
      ownChats,
      otherRegularChats,
      pinnedChats,
      onReorderChannels,
      ownChatIds,
    ],
  );

  const renderChatTreeNode = useCallback(
    (props: {
      node: {
        data: IChannel;
        isDragging: boolean;
      };
      style?: React.CSSProperties;
    }) => {
      if (channelToId(props.node.data) === CLOSED_CHATS_HINT_ID) {
        return (
          <div
            style={{
              ...props.style,
              height: CLOSED_CHATS_HINT_ROW_HEIGHT,
              overflow: "hidden",
              width: "100%",
            }}
          >
            <ClosedChatsHintRow />
          </div>
        );
      }

      const groupEdge = claimGroupEdges.get(channelToId(props.node.data));

      return (
        <div
          style={{
            ...props.style,
            height: DEFAULT_CHAT_ROW_HEIGHT,
            overflow: "hidden",
            width: "100%",
            opacity: props.node.isDragging ? 0.35 : 1,
            position: "relative",
          }}
        >
          {groupEdge && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "#eef1f3",
                borderTopLeftRadius: groupEdge.isFirst ? 8 : 0,
                borderTopRightRadius: groupEdge.isFirst ? 8 : 0,
                borderBottomLeftRadius: groupEdge.isLast ? 8 : 0,
                borderBottomRightRadius: groupEdge.isLast ? 8 : 0,
                pointerEvents: "none",
              }}
            />
          )}
          <ChatThumbnail
            chat={props.node.data}
            isDragging={props.node.isDragging}
            onChatDeleted={onChatDeleted}
            onChatPinned={onChatPinned}
            onChatSelected={(chatId, claimId) => {
              if (dragRef.current) return;
              onChatSelected(chatId, claimId);
            }}
            hasNotification={chatNotifications.has(props.node.data.name)}
          />
        </div>
      );
    },
    [
      chatNotifications,
      onChatDeleted,
      onChatPinned,
      onChatSelected,
      claimGroupEdges,
    ],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        width: "100%",
        pointerEvents: isModalOpen ? "none" : "auto",
      }}
    >
      {showPinned ? (
        loading ? (
          <LoadingChatsMessage />
        ) : pinnedChats.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                px: 1,
                flexDirection: "column",
                gap: 0.5,
                width: "100%",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <Box
                ref={pinnedContainerRef}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  gap: 0,
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <Tree
                  ref={pinnedTreeRef}
                  data={pinnedTreeData}
                  idAccessor={channelToId}
                  width={
                    resolvedPinnedListWidth > 0
                      ? resolvedPinnedListWidth
                      : "100%"
                  }
                  height={pinnedHeight}
                  rowHeight={(node) =>
                    channelToId(node.data) === CLOSED_CHATS_HINT_ID
                      ? CLOSED_CHATS_HINT_ROW_HEIGHT
                      : DEFAULT_CHAT_ROW_HEIGHT
                  }
                  indent={0}
                  dndRootElement={pinnedDndRootElement}
                  disableDrag={(data) =>
                    isModalOpen || channelToId(data) === CLOSED_CHATS_HINT_ID
                  }
                  disableDrop={disablePinnedDrop}
                  renderRow={FlatChatRow}
                  renderCursor={pinnedDropCursor}
                  outerElementType={ChatListOuterElement}
                  renderDragPreview={(previewProps) => (
                    <ChatTreeDragPreview
                      {...previewProps}
                      chats={pinnedChats}
                      chatNotifications={chatNotifications}
                      listWidth={resolvedPinnedListWidth}
                    />
                  )}
                  onMove={handlePinnedTreeMove}
                >
                  {renderChatTreeNode}
                </Tree>
              </Box>
            </Box>

            <Divider />
          </>
        ) : (
          <Box sx={{ px: 1 }}>
            <NoChatsContentMessage
              title={noDataTitleMsg}
              subtitle={noDataSubtitleMsg}
              showClosedChatsHint={showClosedChatsHint}
            />
          </Box>
        )
      ) : null}

      {!showPinned ? (
        <Box
          sx={{
            display: "flex",
            px: 1,
            flexDirection: "column",
            gap: 0.5,
            width: "100%",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              gap: 0,
              width: "100%",
              pointerEvents: isModalOpen ? "none" : "auto",
            }}
          >
            {loading ? (
              <LoadingChatsMessage />
            ) : ownChats.length > 0 ? (
              <>
                <Box
                  ref={containerRef}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Tree
                    ref={treeRef}
                    data={ownTreeData}
                    idAccessor={channelToId}
                    width={listWidth > 0 ? listWidth : "100%"}
                    height={height}
                    rowHeight={(node) =>
                      channelToId(node.data) === CLOSED_CHATS_HINT_ID
                        ? CLOSED_CHATS_HINT_ROW_HEIGHT
                        : DEFAULT_CHAT_ROW_HEIGHT
                    }
                    indent={0}
                    dndRootElement={dndRootElement}
                    disableDrag={(data) =>
                      isModalOpen || channelToId(data) === CLOSED_CHATS_HINT_ID
                    }
                    disableDrop={disableOwnDrop}
                    renderRow={FlatChatRow}
                    renderCursor={ownDropCursor}
                    outerElementType={ChatListOuterElement}
                    renderDragPreview={(previewProps) => (
                      <ChatTreeDragPreview
                        {...previewProps}
                        chats={ownChats}
                        chatNotifications={chatNotifications}
                        listWidth={listWidth}
                      />
                    )}
                    onMove={handleTreeMove}
                    onScroll={
                      !onScroll
                        ? undefined
                        : () => {
                            const el = treeRef.current?.listEl.current;
                            if (!el) return;
                            chatListRef.current = el;
                            onScroll({
                              currentTarget: el,
                            } as React.UIEvent<HTMLDivElement>);
                          }
                    }
                  >
                    {renderChatTreeNode}
                  </Tree>
                </Box>

                {loadingMore && (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      color: "text.secondary",
                      fontSize: "0.875rem",
                    }}
                  >
                    Loading more chats...
                  </Box>
                )}
              </>
            ) : (
              <NoChatsContentMessage
                title={noDataTitleMsg}
                subtitle={noDataSubtitleMsg}
                showClosedChatsHint={showClosedChatsHint}
              />
            )}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
});

const ChatListRenderer = forwardRef<
  VirtualizedChatListHandle,
  ChatListRendererProps
>(function ChatListRenderer(props, ref) {
  return <ChatListRendererContent {...props} ref={ref} />;
});

export default ChatListRenderer;
